import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { ListTodo, Sparkles, Lightbulb } from "lucide-react";
import { UserTakeaway } from "../../../../api/userTakeaways";

interface NeedHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionItem: UserTakeaway | null;
  onBreakDown: () => void;
  onCreateActivity: () => void;
}

const NeedHelpDialog: React.FC<NeedHelpDialogProps> = ({
  open,
  onOpenChange,
  actionItem,
  onBreakDown,
  onCreateActivity,
}) => {
  const { t } = useTranslation();

  if (!actionItem) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Lightbulb className="h-5 w-5 text-yellow-500" />
            {t("action_items.need_help_with_this", "Need help with this?")}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Display the action item content */}
          <div className="p-3 mb-6 rounded-lg bg-muted/50 border">
            <p className="text-sm text-muted-foreground mb-1">{t("action_items.your_action", "Your action item")}:</p>
            <p className="font-medium line-clamp-3">{actionItem.content}</p>
          </div>

          {/* Two options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Break it down */}
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center text-center gap-3 hover:bg-primary/5 hover:border-primary transition-colors"
              onClick={() => {
                onOpenChange(false);
                onBreakDown();
              }}
            >
              <div className="h-12 w-12 rounded-full bg-primary/10 dark:bg-primary/30 flex items-center justify-center">
                <ListTodo className="h-6 w-6 text-primary dark:text-primary/70" />
              </div>
              <div>
                <div className="font-semibold mb-1">
                  {t("action_items.break_it_down", "Break it down")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("action_items.break_it_down_desc", "Divide into smaller actionable tasks")}
                </div>
              </div>
            </Button>

            {/* Create activity */}
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center text-center gap-3 hover:bg-primary/5 hover:border-primary transition-colors"
              onClick={() => {
                onOpenChange(false);
                onCreateActivity();
              }}
            >
              <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <div className="font-semibold mb-1">
                  {t("action_items.create_activity", "Create activity")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("action_items.create_activity_desc", "Build a guided experience for this")}
                </div>
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NeedHelpDialog;
